'use strict';
import forEach from 'lodash/forEach';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/publishReplay';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/switchMap';
import {Observable} from 'rxjs/Observable';
import {getAssetProject, getOrganization, getProject} from '../../app/model/reducers/state';
import {getId} from '../../core-utilities/domain/ReduxUtils';
import {epicErrorHandler} from '../../core-utilities/rx-utils';
import {isApprovableLocked, isCompletableLocked} from '../../projects/services/ParentStatusService';
import {logger} from './logger';
import {
  assetTaskAclUpdatesCache,
  orgAclUpdatesCache,
  projectAclUpdatesCache,
  taskAclUpdatesCache,
} from './PermissionsConfig';
import {isRoleDataChanged, Role} from './Role';
import {RoleBinding} from './RoleBinding';
import {
  assetProjectRoleData,
  assetTaskRoleData,
  isCompositeRoleDataChange,
  orgRoleData,
  projectRoleData,
  taskRoleData,
} from './RoleData';


RxPermissionsService.$inject = [
  '$ngRedux',
  '$cacheFactory',
  '$domain',
  '$permissionsVersions',
  'AuthService',
  'FirebaseUtil',
  'OrganizationDomain',
  'ProjectDomain',
  'TaskDomain',
  'AssetTaskDomain',
];

export function RxPermissionsService(
  $ngRedux,
  $cacheFactory,
  $domain,
  $permissionsVersions,
  AuthService,
  FirebaseUtil,
  OrganizationDomain,
  ProjectDomain,
  TaskDomain,
  AssetTaskDomain,
) {
  const rolesCache = $cacheFactory.get('organizationRoles') || $cacheFactory('organizationRoles');
  const projectRolesCache = $cacheFactory.get('projectRoles') || $cacheFactory('projectRoles');
  const taskRolesCache = $cacheFactory.get('taskRoles') || $cacheFactory('taskRoles');
  const assetTaskRolesCache = $cacheFactory.get('assetTaskRoles') || $cacheFactory('assetTaskRoles');

  const orgRoleBinding = new RoleBinding(
    __orgId$(),
    retrieveRole,
    OrganizationDomain,
    AuthService,
    $domain,
    $permissionsVersions,
    orgAclUpdatesCache,
  );
  const projectRoleBinding = new RoleBinding(
    __projectId$(),
    retrieveProjectRole,
    ProjectDomain,
    AuthService,
    $domain,
    $permissionsVersions,
    projectAclUpdatesCache,
  );
  // const milestoneRoleBinding = new RoleBinding(/*insert getter*/, retrieveMilestoneRole, MilestoneDomain, AuthService);
  // const taskRoleBinding = new RoleBinding(/*insert getter*/, retrieveTaskRole, TaskDomain, AuthService);
  // const assetTaskRoleBinding = new RoleBinding(/*insert getter*/, retrieveAssetTaskRole, AssetTaskDomain, AuthService);

  const userOrgRoleData$ = this.userOrgRoleData$ = buildUserOrgRoleData$()
    .publishReplay(1)
    .refCount();

  const userProjectRoleData$ = this.userProjectRoleData$ = buildUserProjectRoleData$()
    .publishReplay(1)
    .refCount();

  const userAssetProjectRoleData$ = this.userAssetProjectRoleData$ = buildUserAssetProjectRoleData$()
    .publishReplay(1)
    .refCount();

  this.retrieveRole = retrieveRole;
  this.retrieveProjectRole = retrieveProjectRole;
  this.retrieveTaskRole = retrieveTaskRole;
  this.retrieveAssetTaskRole = retrieveAssetTaskRole;

  this.buildUserTaskRoleData$ = buildUserTaskRoleData$;
  this.buildUserAssetTaskRoleData$ = buildUserAssetTaskRoleData$;

  this.hasOrgPermission = hasOrgPermission;
  this.hasProjectPermission = hasProjectPermission;
  this.hasTaskPermission = hasTaskPermission;
  this.hasAssetTaskPermission = hasAssetTaskPermission;

  this.calculatePermissions = calculatePermissions;

  this.initialState = initialState;
  this.orgState = orgState;
  this.projectState = projectState;
  this.projectStateFn = projectStateFn;
  this.buildTaskState = buildTaskState;
  this.taskState = taskState;
  this.taskStateFn = taskStateFn;
  this.buildAssetTaskState = buildAssetTaskState;
  this.assetTaskState = assetTaskState;
  this.assetTaskStateFn = assetTaskStateFn;


  // Calculate permission from RoleData

  /**
   * @param {RoleData} roleData
   * @param {String} permission
   * @returns {Boolean}
   */
  function hasOrgPermission(roleData, permission) {
    return roleData.orgRole ? roleData.orgRole.hasPermission(permission) : false;
  }

  /**
   * @param {RoleData} roleData
   * @param {String} permission
   * @returns {Boolean}
   */
  function hasProjectPermission(roleData, permission) {
    if (roleData.projectIsPrivate) {
      return roleData.projectRole ? roleData.projectRole.hasPermission(permission) : false;
    } else {
      return hasOrgPermission(roleData, permission);
    }
  }

  /**
   * @param {RoleData} roleData
   * @param {String} permission
   * @returns {Boolean}
   */
  function hasTaskPermission(roleData, permission) {
    if (roleData.taskIsPrivate) {
      return roleData.taskRole ? roleData.taskRole.hasPermission(permission) : false;
    } else {
      return hasProjectPermission(roleData, permission);
    }
  }

  /**
   * In the event the asset task does not have a parent task, taskIsPrivate will always be false.
   * Accordingly, hasTaskPermission will correctly skip to hasProjectPermission.
   * @param {RoleData} roleData
   * @param {String} permission
   * @returns {Boolean}
   */
  function hasAssetTaskPermission(roleData, permission) {
    if (roleData.taskIsPrivate) {
      return roleData.taskRole ? roleData.taskRole.hasPermission(permission) : false;
    } else if (roleData.assetTaskIsPrivate) {
      return roleData.assetTaskRole ? roleData.assetTaskRole.hasPermission(permission) : false;
    } else {
      return hasProjectPermission(roleData, permission);
    }
  }


  // Component State helpers

  function initialState() {
    return {
      permissions: {},
      hasPermission: noop,
    };
  }

  function orgState(VIEW_ACTIONS) {
    return roleData => ({
      roleData,
      orgRole: roleData.orgRole,
      permissions: calculatePermissions(VIEW_ACTIONS, roleData, hasOrgPermission),
      hasPermission: permission => hasOrgPermission(roleData, permission),
    });
  }

  function projectState(VIEW_ACTIONS) {
    return roleData => ({
      roleData,
      orgRole: roleData.orgRole,
      projectRole: roleData.projectRole,
      permissions: calculatePermissions(VIEW_ACTIONS, roleData, hasProjectPermission),
      hasPermission: permission => hasProjectPermission(roleData, permission),
    });
  }

  function projectStateFn(get_VIEW_ACTIONS) {
    return roleData => ({
      roleData,
      orgRole: roleData.orgRole,
      projectRole: roleData.projectRole,
      permissions: calculatePermissions(get_VIEW_ACTIONS(roleData), roleData, hasProjectPermission),
      hasPermission: permission => hasProjectPermission(roleData, permission),
    });
  }

  function buildTaskState(roleData, VIEW_ACTIONS) {
    return {
      roleData,
      orgRole: roleData.orgRole,
      projectRole: roleData.projectRole,
      taskRole: roleData.taskRole,
      permissions: calculatePermissions(VIEW_ACTIONS, roleData, hasTaskPermission),
      hasPermission: permission => hasTaskPermission(roleData, permission),
    };
  }

  function taskState(VIEW_ACTIONS) {
    return roleData => buildTaskState(roleData, VIEW_ACTIONS);
  }

  function taskStateFn(get_VIEW_ACTIONS) {
    return roleData => ({
      roleData,
      orgRole: roleData.orgRole,
      projectRole: roleData.projectRole,
      taskRole: roleData.taskRole,
      permissions: calculatePermissions(get_VIEW_ACTIONS(roleData), roleData, hasTaskPermission),
      hasPermission: permission => hasTaskPermission(roleData, permission),
    });
  }

  function buildAssetTaskState(roleData, VIEW_ACTIONS) {
    return {
      roleData,
      orgRole: roleData.orgRole,
      projectRole: roleData.projectRole,
      taskRole: roleData.taskRole,
      assetTaskRole: roleData.assetTaskRole,
      permissions: calculatePermissions(VIEW_ACTIONS, roleData, hasAssetTaskPermission),
      hasPermission: permission => hasAssetTaskPermission(roleData, permission),
    };
  }

  function assetTaskState(VIEW_ACTIONS) {
    return roleData => buildAssetTaskState(roleData, VIEW_ACTIONS);
  }

  function assetTaskStateFn(get_VIEW_ACTIONS) {
    return roleData => ({
      roleData,
      orgRole: roleData.orgRole,
      projectRole: roleData.projectRole,
      taskRole: roleData.taskRole,
      assetTaskRole: roleData.assetTaskRole,
      permissions: calculatePermissions(get_VIEW_ACTIONS(roleData), roleData, hasAssetTaskPermission),
      hasPermission: permission => hasAssetTaskPermission(roleData, permission),
    });
  }


  // Observable Roles

  /**
   * @returns {Observable<Role>}
   */
  function orgRole$() {
    return orgRoleBinding.role$()
      .distinctUntilChanged(isRoleDataChanged)
      .catch(epicErrorHandler(logger, 'orgRole$'));
  }

  /**
   * @returns {Observable<Role>}
   */
  function projectRole$() {
    return projectRoleBinding.role$()
      .distinctUntilChanged(isRoleDataChanged)
      .catch(epicErrorHandler(logger, 'projectRole$'));
  }

  /**
   * @param {Observable<String>} modelId$
   * @returns {Observable<Role>}
   */
  function taskRole$(modelId$) {
    const taskRoleBinding = new RoleBinding(
      modelId$,
      retrieveTaskRole,
      TaskDomain,
      AuthService,
      $domain,
      $permissionsVersions,
      taskAclUpdatesCache,
    );
    return taskRoleBinding.role$()
      .distinctUntilChanged(isRoleDataChanged)
      .catch(epicErrorHandler(logger, 'taskRole$'));
  }

  /**
   * @param {Observable<String>} modelId$
   * @returns {Observable<Role>}
   */
  function assetTaskRole$(modelId$) {
    const assetTaskRoleBinding = new RoleBinding(
      modelId$,
      retrieveAssetTaskRole,
      AssetTaskDomain,
      AuthService,
      $domain,
      $permissionsVersions,
      assetTaskAclUpdatesCache,
    );
    return assetTaskRoleBinding.role$()
      .distinctUntilChanged(isRoleDataChanged)
      .catch(epicErrorHandler(logger, 'assetTaskRole$'));
  }

  /**
   * @return {Observable<RoleData>}
   */
  function buildUserOrgRoleData$() {
    return $ngRedux.state$
      .map(state => getId(getOrganization(state)))
      .distinctUntilChanged()
      .switchMap(id => id ? orgRole$() : Observable.of(null))
      .map(role => orgRoleData(role))
      .distinctUntilChanged(isCompositeRoleDataChange)
      .catch(epicErrorHandler(logger, 'userOrgRoleData$'));
  }

  /**
   * @return {Observable<RoleData>}
   */
  function buildUserProjectRoleData$() {
    const projectRoleData$ = $ngRedux.state$
      .map(getProject)
      .distinctUntilChanged(__isIdOrCompletableLockedChanged)
      .switchMap(project => {
        if (project) {
          const projectIsPrivate = project.isPrivate;
          const projectIsLocked = isCompletableLocked(project);
          return projectRole$()
            .map(role => projectRoleData(role, projectIsPrivate, projectIsLocked));
        } else {
          return Observable.of(projectRoleData(null, false, false));
        }
      });

    return Observable.combineLatest(
      userOrgRoleData$,
      projectRoleData$,
    )
      .map(data => Object.assign({}, ...data))
      .distinctUntilChanged(isCompositeRoleDataChange)
      .catch(epicErrorHandler(logger, 'userProjectRoleData$'));
  }

  /**
   * @return {Observable<RoleData>}
   */
  function buildUserAssetProjectRoleData$() {
    const assetProjectRoleData$ = $ngRedux.state$
      .map(getAssetProject)
      .distinctUntilChanged(__isIdOrCompletableLockedChanged)
      .switchMap(project => {
        if (project) {
          return Observable.of(assetProjectRoleData(null, false, isCompletableLocked(project)));
        } else {
          return Observable.of(assetProjectRoleData(null, false, false));
        }
      });

    return Observable.combineLatest(
      userOrgRoleData$,
      userProjectRoleData$,
      assetProjectRoleData$,
    )
      .map(data => Object.assign({}, ...data))
      .distinctUntilChanged(isCompositeRoleDataChange)
      .catch(epicErrorHandler(logger, 'buildUserAssetProjectRoleData$'));
  }

  /**
   * @param {Observable<Object|TaskDomain>} task$ - An observable stream of TaskDomain objects. They may either be the raw data, or the instance of the class.
   * @return {Observable<RoleData>}
   */
  function buildUserTaskRoleData$(task$) {
    return Observable.combineLatest(
      userOrgRoleData$,
      userProjectRoleData$,
      __buildTaskRoleData$(task$),
    )
      .map(data => Object.assign({}, ...data))
      .distinctUntilChanged(isCompositeRoleDataChange)
      .catch(epicErrorHandler(logger, 'userTaskRoleData$'));
  }

  function __buildTaskRoleData$(task$) {
    return task$
      .distinctUntilChanged(__isIdOrApprovableLockedChanged)
      .switchMap(task => {
        if (task) {
          const taskIsPrivate = task.isPrivate;
          const taskIsLocked = isApprovableLocked(task);
          return taskRole$(Observable.of(getId(task)))
            .map(role => taskRoleData(role, taskIsPrivate, taskIsLocked));
        } else {
          return Observable.of(taskRoleData(null, false, false));
        }
      });
  }

  /**
   * @param {Observable<Object|TaskDomain>} assetTask$ - An observable stream of AssetTaskDomain objects. They may either be the raw data, or the instance of the class.
   * @param {?Observable<Object|TaskDomain>} parentTask$ - An observable stream of TaskDomain objects. They may either be the raw data, or the instance of the class.
   * @return {Observable<RoleData>}
   */
  function buildUserAssetTaskRoleData$(assetTask$, parentTask$) {
    const assetTaskRoleData$ = assetTask$
      .distinctUntilChanged(__isIdOrApprovableLockedChanged)
      .switchMap(task => {
        if (task) {
          const assetTaskIsPrivate = task.isPrivate;
          const assetTaskIsLocked = isApprovableLocked(task);
          return assetTaskRole$(Observable.of(getId(task)))
            .map(role => (assetTaskRoleData(role, assetTaskIsPrivate, assetTaskIsLocked)));
        } else {
          return Observable.of(assetTaskRoleData(null, false, false));
        }
      });

    const taskRoleData$ = parentTask$
      ? __buildTaskRoleData$(parentTask$)
      : Observable.of(taskRoleData(null, false, false));

    return Observable.combineLatest(
      userOrgRoleData$,
      userProjectRoleData$,
      userAssetProjectRoleData$,
      taskRoleData$,
      assetTaskRoleData$,
    )
      .map(data => Object.assign({}, ...data))
      .distinctUntilChanged(isCompositeRoleDataChange)
      .catch(epicErrorHandler(logger, 'userAssetTaskRoleData$'));
  }


  function retrieveRole(roleId) {
    return __retrieveRole(roleId, '', rolesCache);
  }

  function retrieveProjectRole(roleId) {
    return __retrieveRole(roleId, '-projects', projectRolesCache);
  }

  function retrieveTaskRole(roleId) {
    return __retrieveRole(roleId, '-tasks', taskRolesCache);
  }

  function retrieveAssetTaskRole(roleId) {
    return __retrieveRole(roleId, '-asset-tasks', assetTaskRolesCache);
  }

  function __retrieveRole(roleId, type, roleCache) {
    const role = roleCache.get(roleId);
    return role
      ? Promise.resolve(role)
      : FirebaseUtil.firebaseRef(`_roles${type}-meta/${roleId}/links/permissions`)
        .once('value')
        .then(ss => {
          const role = new Role(ss.val());
          roleCache.put(roleId, role);
          return role;
        });
  }

  /**
   * Retrieve the role corresponding to the model
   *
   * Examples:
   *   if the model is an OrganizationDomain, use $permissions.retrieveRole
   *   if the model is a ProjectDomain, use $permissions.retrieveProjectRole
   *
   * @callback hasPermission
   * @param {RoleData} roleData
   * @param {String} permission
   * @returns {Boolean}
   */

  /**
   * Helper method to combine the latest {@link RoleData} with the desired view actions map.
   * @param {Object} viewActions Map of action name to required permission
   * @param {RoleData} roleData
   * @param {hasPermission} hasPermission
   * @return {Object<String, Boolean>}
   */
  function calculatePermissions(viewActions, roleData, hasPermission) {
    const permissions = {};
    forEach(viewActions, (permission, action) => {
      if (isString(permission)) {
        permissions[action] = hasPermission(roleData, permission);
      } else if (isFunction(permission)) {
        permissions[action] = hasPermission(roleData, permission());
      }
    });
    return permissions;
  }

  function __orgId$() {
    return $ngRedux.state$.map(state => getId(getOrganization(state)));
  }

  function __projectId$() {
    return $ngRedux.state$.map(state => getId(getProject(state)));
  }

  function __isIdOrCompletableLockedChanged(a, b) {
    if (!a || !b) {
      return false;
    } else {
      return (
        getId(a) === getId(b.userId)
        && isCompletableLocked(a) === isCompletableLocked(b)
      );
    }
  }

  function __isIdOrApprovableLockedChanged(a, b) {
    if (!a || !b) {
      return false;
    } else {
      return (
        getId(a) === getId(b.userId)
        && isApprovableLocked(a) === isApprovableLocked(b)
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/auth/services/RxPermissionsService.js